import { useState, useEffect } from 'react';
import { Shield, Users, FileText, Crown, Search, AlertTriangle, Wallet, Settings, BarChart3, Tag, Gift, Star, TrendingUp, Lock, Home, UserPlus, Eye, ChevronDown, ChevronUp, Loader2, Target, CheckSquare, Ban, Trash2, Activity, CreditCard, KeyRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { useLegalDocuments } from '@/hooks/useLegalDocuments';
import { supabase } from '@/integrations/supabase/client';

import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AdminReferrals } from '@/components/admin/AdminReferrals';
import { AdminPromoCodes } from '@/components/admin/AdminPromoCodes';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useAnalyticsSettings } from '@/hooks/useAnalyticsSettings';
import { useYookassaSettings } from '@/hooks/useYookassaSettings';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

interface UserWithRole {
  id: string;
  email: string;
  display_name: string | null;
  role: string | null;
  created_at: string;
  read_only_until?: string | null;
}

interface AppStats {
  totalUsers: number;
  activeUsers: number;
  proUsers: number;
  totalHabits: number;
  totalTasks: number;
  totalStars: number;
  rewardsSpent: number;
}

export default function Admin() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { documents, isAdmin, adminLoading, updateDocument, loading: docsLoading } = useLegalDocuments();

  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [roleToAssign, setRoleToAssign] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [banDays, setBanDays] = useState<number>(7);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Stats
  const [stats, setStats] = useState<AppStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Document editing
  const [editingDoc, setEditingDoc] = useState<string | null>(null);
  const [docTitle, setDocTitle] = useState('');
  const [docContent, setDocContent] = useState('');

  // Promo codes - now in AdminPromoCodes component

  // Settings sections
  const [bonusSettingsOpen, setBonusSettingsOpen] = useState(false);
  const [limitSettingsOpen, setLimitSettingsOpen] = useState(false);
  const [accessControlOpen, setAccessControlOpen] = useState(false);
  const [accessControlConfirmOpen, setAccessControlConfirmOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [yookassaOpen, setYookassaOpen] = useState(false);

  // App Settings
  const {
    localAccessControl,
    updateLocalAccessControl,
    saveAccessControl,
    resetLocalAccessControl,
    loading: accessLoading,
    saving: accessSaving,
    hasChanges: accessHasChanges,
  } = useAppSettings();

  const {
    localSettings: analyticsLocal,
    updateLocal: updateAnalyticsLocal,
    save: saveAnalytics,
    reset: resetAnalytics,
    saving: analyticsSaving,
    hasChanges: analyticsHasChanges,
  } = useAnalyticsSettings();

  const {
    localSettings: yooLocal,
    updateLocal: updateYooLocal,
    save: saveYoo,
    reset: resetYoo,
    saving: yooSaving,
    hasChanges: yooHasChanges,
    loading: yooLoading,
  } = useYookassaSettings();

  const isRussian = language === 'ru';
  const [adminChecked, setAdminChecked] = useState(false);
  const [authLoaded, setAuthLoaded] = useState(false);

  // Admin access is determined exclusively by the `admin` role in user_roles
  // (checked server-side via has_role()). Email-based bypasses removed.
  const hasAdminAccess = Boolean(isAdmin);

  // Wait for auth to fully load
  useEffect(() => {
    if (user !== undefined) {
      setAuthLoaded(true);
    }
  }, [user]);

  // Check if user can access admin panel - wait for docs and role check to fully load
  useEffect(() => {
    // Don't redirect until auth, docs, AND admin check are all fully loaded
    if (!docsLoading && !adminLoading && authLoaded) {
      setAdminChecked(true);

      if (!user) {
        // User is not logged in
        navigate('/auth');
        return;
      }

      if (!hasAdminAccess) {
        // User is logged in but not allowed
        navigate('/');
      }
    }
  }, [docsLoading, adminLoading, authLoaded, user, hasAdminAccess, navigate]);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!hasAdminAccess) return;
      setStatsLoading(true);

      try {
        // Get user count
        const { count: userCount } = await supabase.from('profiles').select('*', { count: 'exact', head: true });

        // Get subscription stats
        const { data: subs } = await supabase.from('subscriptions').select('plan').eq('plan', 'pro');

        // Get star transactions
        const { data: starData } = await supabase.from('user_stars').select('total_stars');

        const totalStars = starData?.reduce((sum, u) => sum + (u.total_stars || 0), 0) || 0;

        // Get habits count
        const { count: habitsCount } = await supabase.from('habits').select('*', { count: 'exact', head: true });

        // Get tasks count
        const { count: tasksCount } = await supabase.from('tasks').select('*', { count: 'exact', head: true });

        setStats({
          totalUsers: userCount || 0,
          activeUsers: Math.floor((userCount || 0) * 0.7), // Estimate
          proUsers: subs?.length || 0,
          totalHabits: habitsCount || 0,
          totalTasks: tasksCount || 0,
          totalStars: totalStars,
          rewardsSpent: 0, // TODO: calculate from purchased_rewards
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [hasAdminAccess]);

  // Fetch users with their roles
  useEffect(() => {
    const fetchUsers = async () => {
      if (!hasAdminAccess) return;

      try {
        // Get profiles
        const { data: profiles, error: profilesError } = await supabase
          .from('public_profiles')
          .select('user_id, display_name, created_at');

        if (profilesError) throw profilesError;

        // Get roles
        const { data: roles, error: rolesError } = await supabase.from('user_roles').select('user_id, role');

        if (rolesError) throw rolesError;

        // Combine data
        const usersWithRoles: UserWithRole[] = (profiles || []).map((profile) => {
          const userRole = roles?.find((r) => r.user_id === profile.user_id);
          return {
            id: profile.user_id,
            email: '',
            display_name: profile.display_name,
            role: userRole?.role || null,
            created_at: profile.created_at,
          };
        });

        setUsers(usersWithRoles);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error(isRussian ? 'Ошибка загрузки пользователей' : 'Error loading users');
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [hasAdminAccess, isRussian]);

  const handleRoleChange = async () => {
    if (!selectedUser || !roleToAssign) return;

    try {
      if (roleToAssign === 'remove') {
        const { error } = await supabase.from('user_roles').delete().eq('user_id', selectedUser.id);
        if (error) throw error;
      } else {
        // First delete existing roles, then insert new one
        await supabase.from('user_roles').delete().eq('user_id', selectedUser.id);
        const { error } = await supabase.from('user_roles').insert({
          user_id: selectedUser.id,
          role: roleToAssign as any,
        });
        if (error) throw error;
      }

      setUsers(
        users.map((u) =>
          u.id === selectedUser.id
            ? { ...u, role: roleToAssign === 'remove' ? null : roleToAssign }
            : u,
        ),
      );

      toast.success(isRussian ? 'Роль обновлена' : 'Role updated');
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(isRussian ? 'Ошибка обновления роли' : 'Error updating role');
    }

    setConfirmDialogOpen(false);
    setSelectedUser(null);
    setRoleToAssign('');
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;
    try {
      const readOnlyUntil = new Date(Date.now() + banDays * 24 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from('profiles')
        .update({ read_only_until: readOnlyUntil })
        .eq('user_id', selectedUser.id);
      if (error) throw error;

      setUsers(users.map((u) =>
        u.id === selectedUser.id ? { ...u, read_only_until: readOnlyUntil } : u,
      ));
      toast.success(isRussian ? `Пользователь заблокирован на ${banDays} дн.` : `User banned for ${banDays} days`);
    } catch (error) {
      console.error('Error banning user:', error);
      toast.error(isRussian ? 'Ошибка блокировки' : 'Error banning user');
    }
    setBanDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      // Save fingerprint to banned list before deletion
      const { data: fingerprints } = await supabase
        .from('device_fingerprints')
        .select('fingerprint_hash')
        .eq('user_id', selectedUser.id);

      if (fingerprints && fingerprints.length > 0) {
        for (const fp of fingerprints) {
          await supabase.from('banned_fingerprints').insert({
            fingerprint_hash: fp.fingerprint_hash,
            banned_by: user?.id,
            reason: 'User deleted by admin',
          });
        }
      }

      // Delete user data from all tables
      await supabase.from('user_roles').delete().eq('user_id', selectedUser.id);
      await supabase.from('profiles').delete().eq('user_id', selectedUser.id);

      setUsers(users.filter((u) => u.id !== selectedUser.id));
      toast.success(isRussian ? 'Пользователь удалён' : 'User deleted');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(isRussian ? 'Ошибка удаления' : 'Error deleting user');
    }
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDocumentSave = async () => {
    if (!editingDoc) return;

    const success = await updateDocument(editingDoc, docTitle, docContent);
    if (success) {
      setEditingDoc(null);
    }
  };

  const startEditingDocument = (type: string) => {
    const doc = documents.find((d) => d.type === type);
    if (doc) {
      setEditingDoc(type);
      setDocTitle(doc.title);
      setDocContent(doc.content);
    }
  };

  const filteredUsers = users.filter(
    (u) => u.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) || u.id.includes(searchQuery),
  );

  const getRoleBadgeColor = (role: string | null) => {
    switch (role) {
      case 'admin':
        return 'bg-red-500/10 text-red-500 border-red-500/30';
      case 'moderator':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/30';
      case 'team':
      case 'team_member':
      case 'team_owner':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
      case 'focus':
        return 'bg-green-500/10 text-green-500 border-green-500/30';
      case 'profi':
        return 'bg-indigo-500/10 text-indigo-500 border-indigo-500/30';
      case 'premium':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/30';
      case 'user':
        return 'bg-green-500/10 text-green-500 border-green-500/30';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getRoleLabel = (role: string | null) => {
    switch (role) {
      case 'focus': return 'Фокус';
      case 'profi': return 'Профи';
      case 'premium': return 'Премиум';
      case 'team_member': return 'Team (member)';
      case 'team_owner': return 'Team (owner)';
      case 'team': return 'Team';
      default: return role || (isRussian ? 'Нет роли' : 'No role');
    }
  };

  if (docsLoading || adminLoading || !adminChecked || !authLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (!hasAdminAccess || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        <PageHeader
          showTitle
          icon={<Shield className="w-5 h-5 text-red-500" />}
          iconBgClass="bg-red-500/10"
          title={isRussian ? 'Панель администратора' : 'Admin Panel'}
          subtitle={isRussian ? 'Полное управление приложением' : 'Full app management'}
        />

        <Tabs defaultValue="stats" className="mt-6">
          <TabsList className="grid w-full grid-cols-5 h-auto">
            <TabsTrigger value="stats" className="flex flex-col gap-1 py-2 text-xs">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">{isRussian ? 'Статистика' : 'Stats'}</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex flex-col gap-1 py-2 text-xs">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">{isRussian ? 'Пользователи' : 'Users'}</span>
            </TabsTrigger>
            <TabsTrigger value="referrals" className="flex flex-col gap-1 py-2 text-xs">
              <Wallet className="w-4 h-4" />
              <span className="hidden sm:inline">{isRussian ? 'Рефералы' : 'Referrals'}</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex flex-col gap-1 py-2 text-xs">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">{isRussian ? 'Настройки' : 'Settings'}</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex flex-col gap-1 py-2 text-xs">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">{isRussian ? 'Документы' : 'Docs'}</span>
            </TabsTrigger>
          </TabsList>

          {/* Stats Tab */}
          <TabsContent value="stats" className="mt-4 space-y-4">
            {statsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : stats && (
              <>
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-blue-500/20">
                            <Users className="w-5 h-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{stats.totalUsers}</p>
                            <p className="text-xs text-muted-foreground">{isRussian ? 'Всего пользователей' : 'Total Users'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-amber-500/20">
                            <Crown className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{stats.proUsers}</p>
                            <p className="text-xs text-muted-foreground">{isRussian ? 'PRO подписчики' : 'PRO Users'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-green-500/20">
                            <Target className="w-5 h-5 text-green-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{stats.totalHabits}</p>
                            <p className="text-xs text-muted-foreground">{isRussian ? 'Привычек' : 'Habits'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-purple-500/20">
                            <CheckSquare className="w-5 h-5 text-purple-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{stats.totalTasks}</p>
                            <p className="text-xs text-muted-foreground">{isRussian ? 'Задач' : 'Tasks'}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>

                {/* Additional Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      {isRussian ? 'Дополнительная статистика' : 'Additional Stats'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm">{isRussian ? 'Всего звёзд заработано' : 'Total Stars Earned'}</span>
                      </div>
                      <span className="font-bold">{stats.totalStars.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-pink-500" />
                        <span className="text-sm">{isRussian ? 'Наград потрачено' : 'Rewards Spent'}</span>
                      </div>
                      <span className="font-bold">{stats.rewardsSpent}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <span className="text-sm">{isRussian ? 'Конверсия в PRO' : 'PRO Conversion'}</span>
                      </div>
                      <span className="font-bold">
                        {stats.totalUsers > 0 ? ((stats.proUsers / stats.totalUsers) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{isRussian ? 'Управление ролями' : 'Role Management'}</span>
                  <Badge variant="outline">{users.length} {isRussian ? 'пользователей' : 'users'}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={isRussian ? 'Поиск по имени или ID...' : 'Search by name or ID...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {usersLoading ? (
                      <div className="text-center py-8 text-muted-foreground">
                        {t('loading')}
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        {isRussian ? 'Пользователи не найдены' : 'No users found'}
                      </div>
                    ) : (
                      filteredUsers.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              {u.role === 'admin' ? (
                                <Crown className="w-5 h-5 text-amber-500" />
                              ) : (
                                <span className="text-sm font-medium text-primary">
                                  {(u.display_name || 'U')[0].toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {u.display_name || 'Unknown'}
                              </p>
                              <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                                ID: {u.id.slice(0, 8)}...
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={getRoleBadgeColor(u.role)}>
                              {getRoleLabel(u.role)}
                            </Badge>
                            {u.read_only_until && new Date(u.read_only_until) > new Date() && (
                              <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30 text-[10px]">
                                <Ban className="w-3 h-3 mr-1" />
                                {isRussian ? 'Бан' : 'Banned'}
                              </Badge>
                            )}
                            <Select
                              value=""
                              onValueChange={(value) => {
                                setSelectedUser(u);
                                if (value === 'ban') {
                                  setBanDialogOpen(true);
                                } else if (value === 'delete_user') {
                                  setDeleteDialogOpen(true);
                                } else {
                                  setRoleToAssign(value);
                                  setConfirmDialogOpen(true);
                                }
                              }}
                            >
                              <SelectTrigger
                                className="w-[130px] h-8 text-xs"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <SelectValue placeholder={isRussian ? 'Действие' : 'Action'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="focus">{isRussian ? 'Фокус (тариф)' : 'Focus (plan)'}</SelectItem>
                                <SelectItem value="profi">{isRussian ? 'Профи (тариф)' : 'Profi (plan)'}</SelectItem>
                                <SelectItem value="premium">{isRussian ? 'Премиум (тариф)' : 'Premium (plan)'}</SelectItem>
                                <SelectItem value="team_member">Team (member)</SelectItem>
                                <SelectItem value="team_owner">Team (owner)</SelectItem>
                                <SelectItem value="moderator">Moderator</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="remove">{isRussian ? 'Удалить роль' : 'Remove role'}</SelectItem>
                                <SelectItem value="ban" className="text-amber-500">
                                  <span className="flex items-center gap-1">
                                    <Ban className="w-3 h-3" />
                                    {isRussian ? 'Бан (read-only)' : 'Ban (read-only)'}
                                  </span>
                                </SelectItem>
                                <SelectItem value="delete_user" className="text-destructive">
                                  <span className="flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" />
                                    {isRussian ? 'Удалить пользователя' : 'Delete user'}
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referrals Tab */}
          <TabsContent value="referrals" className="mt-4">
            <AdminReferrals />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-4 space-y-4">
            {/* Access Control */}
            <Collapsible open={accessControlOpen} onOpenChange={setAccessControlOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-red-500" />
                        {isRussian ? 'Управление доступом' : 'Access Control'}
                      </div>
                      {accessControlOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </CardTitle>
                    <CardDescription>
                      {isRussian ? 'Настройки доступа к приложению' : 'App access settings'}
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    {accessLoading ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {/* Start Page */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Home className="w-4 h-4 text-muted-foreground" />
                            <Label>{isRussian ? 'Стартовая страница' : 'Start Page'}</Label>
                          </div>
                          <Select
                            value={localAccessControl.start_page}
                            onValueChange={(value) => updateLocalAccessControl({ start_page: value as 'dashboard' | 'auth' })}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="dashboard">
                                {isRussian ? 'Главная' : 'Dashboard'}
                              </SelectItem>
                              <SelectItem value="auth">
                                {isRussian ? 'Авторизация' : 'Auth'}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Registration */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <UserPlus className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <Label>{isRussian ? 'Новые регистрации' : 'New Registrations'}</Label>
                              <p className="text-xs text-muted-foreground">
                                {isRussian ? 'Разрешить регистрацию новых пользователей' : 'Allow new user registrations'}
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={localAccessControl.registration_enabled}
                            onCheckedChange={(checked) => updateLocalAccessControl({ registration_enabled: checked })}
                          />
                        </div>

                        {/* Guest Access */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <Label>{isRussian ? 'Гостевой доступ' : 'Guest Access'}</Label>
                              <p className="text-xs text-muted-foreground">
                                {isRussian ? 'Разрешить вход без авторизации' : 'Allow access without login'}
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={localAccessControl.guest_access_enabled}
                            onCheckedChange={(checked) => updateLocalAccessControl({ guest_access_enabled: checked })}
                          />
                        </div>

                        {/* Action Buttons */}
                        {accessHasChanges && (
                          <div className="flex gap-2 mt-2">
                            <Button 
                              variant="outline"
                              className="flex-1" 
                              onClick={resetLocalAccessControl}
                              disabled={accessSaving}
                            >
                              {isRussian ? 'Отменить' : 'Cancel'}
                            </Button>
                            <Button 
                              className="flex-1" 
                              onClick={() => setAccessControlConfirmOpen(true)}
                              disabled={accessSaving}
                            >
                              {accessSaving ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  {isRussian ? 'Сохранение...' : 'Saving...'}
                                </>
                              ) : (
                                isRussian ? 'Сохранить' : 'Save'
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Bonus Settings */}
            <Collapsible open={bonusSettingsOpen} onOpenChange={setBonusSettingsOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        {isRussian ? 'Бонусная программа' : 'Bonus Program'}
                      </div>
                      {bonusSettingsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </CardTitle>
                    <CardDescription>
                      {isRussian ? 'Настройки начисления звёзд и бонусов' : 'Stars and bonus settings'}
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="grid gap-4">
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Звёзды за ежедневный вход' : 'Stars for daily login'}</Label>
                        <Input type="number" defaultValue="1" className="w-20 text-right" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Звёзды за выполнение привычки' : 'Stars for habit completion'}</Label>
                        <Input type="number" defaultValue="1" className="w-20 text-right" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Звёзды за выполнение задачи' : 'Stars for task completion'}</Label>
                        <Input type="number" defaultValue="1" className="w-20 text-right" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Множитель за серию (7 дней)' : 'Streak multiplier (7 days)'}</Label>
                        <Input type="number" defaultValue="2" className="w-20 text-right" />
                      </div>
                    </div>
                    <Button className="w-full">{isRussian ? 'Сохранить настройки' : 'Save Settings'}</Button>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Limit Settings */}
            <Collapsible open={limitSettingsOpen} onOpenChange={setLimitSettingsOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        {isRussian ? 'Лимиты FREE тарифа' : 'FREE Tier Limits'}
                      </div>
                      {limitSettingsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </CardTitle>
                    <CardDescription>
                      {isRussian ? 'Ограничения для бесплатных пользователей' : 'Restrictions for free users'}
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="grid gap-4">
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Макс. привычек' : 'Max habits'}</Label>
                        <Input type="number" defaultValue="3" className="w-20 text-right" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Макс. задач' : 'Max tasks'}</Label>
                        <Input type="number" defaultValue="5" className="w-20 text-right" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Макс. операций/месяц' : 'Max operations/month'}</Label>
                        <Input type="number" defaultValue="15" className="w-20 text-right" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Разрешить подзадачи' : 'Allow subtasks'}</Label>
                        <Switch defaultChecked={false} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Разрешить вложения' : 'Allow attachments'}</Label>
                        <Switch defaultChecked={false} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>{isRussian ? 'Разрешить регулярность' : 'Allow recurrence'}</Label>
                        <Switch defaultChecked={false} />
                      </div>
                    </div>
                    <Button className="w-full">{isRussian ? 'Сохранить настройки' : 'Save Settings'}</Button>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Analytics Codes */}
            <Collapsible open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4 text-primary" />
                        {isRussian ? 'Аналитика и вебмастер' : 'Analytics & Webmaster'}
                      </div>
                      {analyticsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </CardTitle>
                    <CardDescription>
                      {isRussian ? 'Яндекс Метрика и Вебмастер' : 'Yandex Metrika & Webmaster'}
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="grid gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-sm">
                          {isRussian ? 'ID счётчика Яндекс Метрики' : 'Yandex Metrika Counter ID'}
                        </Label>
                        <Input
                          value={analyticsLocal.yandex_metrika_id}
                          onChange={e => updateAnalyticsLocal({ yandex_metrika_id: e.target.value })}
                          placeholder="12345678"
                          className="font-mono"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          {isRussian
                            ? 'Только числовой ID. Скрипт счётчика будет автоматически встроен в приложение (clickmap, webvisor, trackHash).'
                            : 'Numeric ID only. The counter script will be auto-injected (clickmap, webvisor, trackHash).'}
                        </p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">
                          {isRussian ? 'Код подтверждения Яндекс Вебмастер' : 'Yandex Webmaster Verification'}
                        </Label>
                        <Input
                          value={analyticsLocal.yandex_webmaster_verification}
                          onChange={e => updateAnalyticsLocal({ yandex_webmaster_verification: e.target.value })}
                          placeholder="abc123def456"
                          className="font-mono"
                        />
                        <p className="text-[11px] text-muted-foreground">
                          {isRussian
                            ? 'Значение из meta-тега yandex-verification. Будет добавлено в <head> приложения.'
                            : 'Value from the yandex-verification meta tag. Will be injected into the app <head>.'}
                        </p>
                      </div>
                    </div>
                    {analyticsHasChanges && (
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={resetAnalytics}
                          disabled={analyticsSaving}
                        >
                          {isRussian ? 'Отменить' : 'Cancel'}
                        </Button>
                        <Button
                          className="flex-1"
                          onClick={saveAnalytics}
                          disabled={analyticsSaving}
                        >
                          {analyticsSaving ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              {isRussian ? 'Сохранение...' : 'Saving...'}
                            </>
                          ) : (
                            isRussian ? 'Сохранить' : 'Save'
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Promo Codes */}
            <AdminPromoCodes />

            {/* YooKassa Settings */}
            <Collapsible open={yookassaOpen} onOpenChange={setYookassaOpen}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-primary" />
                        {isRussian ? 'ЮKassa (платежи)' : 'YooKassa (Payments)'}
                      </div>
                      {yookassaOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </CardTitle>
                    <CardDescription>
                      {isRussian ? 'Настройка тестового и боевого режима оплаты' : 'Configure test and live payment mode'}
                    </CardDescription>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    {yooLoading ? (
                      <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                    ) : (
                      <div className="grid gap-4">
                        <div className="flex items-center justify-between">
                          <Label>{isRussian ? 'Режим' : 'Mode'}</Label>
                          <Select value={yooLocal.mode} onValueChange={(v) => updateYooLocal({ mode: v as 'test' | 'live' })}>
                            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="test">{isRussian ? 'Тестовый' : 'Test'}</SelectItem>
                              <SelectItem value="live">{isRussian ? 'Боевой' : 'Live'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-sm">{isRussian ? 'Test Shop ID' : 'Test Shop ID'}</Label>
                          <Input value={yooLocal.test_shop_id} onChange={e => updateYooLocal({ test_shop_id: e.target.value })} placeholder="123456" className="font-mono" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-sm">{isRussian ? 'Live Shop ID' : 'Live Shop ID'}</Label>
                          <Input value={yooLocal.live_shop_id} onChange={e => updateYooLocal({ live_shop_id: e.target.value })} placeholder="987654" className="font-mono" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-sm">{isRussian ? 'Return URL' : 'Return URL'}</Label>
                          <Input value={yooLocal.return_url} onChange={e => updateYooLocal({ return_url: e.target.value })} placeholder="https://top-focus.ru/profile" className="font-mono" />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-sm">{isRussian ? 'Webhook URL' : 'Webhook URL'}</Label>
                          <Input value={yooLocal.webhook_url} onChange={e => updateYooLocal({ webhook_url: e.target.value })} placeholder="https://.../yookassa-webhook" className="font-mono" />
                        </div>

                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground space-y-1">
                          <div className="flex items-center gap-2 text-amber-500 font-medium">
                            <KeyRound className="w-3.5 h-3.5" />
                            {isRussian ? 'Секретные ключи (YOOKASSA_SHOP_ID / YOOKASSA_SECRET_KEY)' : 'Secret keys (YOOKASSA_SHOP_ID / YOOKASSA_SECRET_KEY)'}
                          </div>
                          <p>{isRussian
                            ? 'Секретные ключи хранятся в Edge Function Secrets и недоступны клиенту. Добавьте их через панель Supabase → Functions → Secrets.'
                            : 'Secret keys are stored in Edge Function Secrets and are not exposed to the client. Add them via Supabase → Functions → Secrets.'}
                          </p>
                          <a
                            href="https://supabase.com/dashboard/project/jexrtsyokhegjxnvqjur/settings/functions"
                            target="_blank" rel="noreferrer"
                            className="text-primary underline"
                          >
                            {isRussian ? 'Открыть настройки Edge Functions →' : 'Open Edge Function settings →'}
                          </a>
                        </div>

                        {yooHasChanges && (
                          <div className="flex gap-2 mt-2">
                            <Button variant="outline" className="flex-1" onClick={resetYoo} disabled={yooSaving}>
                              {isRussian ? 'Отменить' : 'Cancel'}
                            </Button>
                            <Button className="flex-1" onClick={saveYoo} disabled={yooSaving}>
                              {yooSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{isRussian ? 'Сохранение...' : 'Saving...'}</> : (isRussian ? 'Сохранить' : 'Save')}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {isRussian ? 'Правовые документы' : 'Legal Documents'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editingDoc ? (
                  <div className="space-y-4">
                    <Input
                      value={docTitle}
                      onChange={(e) => setDocTitle(e.target.value)}
                      placeholder={isRussian ? 'Заголовок' : 'Title'}
                    />
                    <Textarea
                      value={docContent}
                      onChange={(e) => setDocContent(e.target.value)}
                      placeholder={isRussian ? 'Содержание (Markdown)' : 'Content (Markdown)'}
                      className="min-h-[300px] font-mono text-sm"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleDocumentSave}>
                        {isRussian ? 'Сохранить' : 'Save'}
                      </Button>
                      <Button variant="outline" onClick={() => setEditingDoc(null)}>
                        {t('cancel')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-sm">{doc.title}</p>
                          <p className="text-xs text-muted-foreground">
                            v{doc.version} • {new Date(doc.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditingDocument(doc.type)}
                        >
                          {t('edit')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Role Change Confirmation */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {isRussian ? 'Изменить роль?' : 'Change role?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRussian 
                ? `Вы собираетесь ${roleToAssign === 'remove' ? 'удалить роль у' : `назначить роль "${roleToAssign}" для`} пользователя "${selectedUser?.display_name || 'Unknown'}".`
                : `You are about to ${roleToAssign === 'remove' ? 'remove the role from' : `assign the role "${roleToAssign}" to`} user "${selectedUser?.display_name || 'Unknown'}".`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRoleChange}>
              {isRussian ? 'Подтвердить' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Access Control Confirmation */}
      <AlertDialog open={accessControlConfirmOpen} onOpenChange={setAccessControlConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {isRussian ? 'Изменить настройки доступа?' : 'Change access settings?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                {isRussian 
                  ? 'Вы собираетесь изменить настройки доступа к приложению. Это может повлиять на работу приложения для всех пользователей.'
                  : 'You are about to change the app access settings. This may affect the app availability for all users.'
                }
              </p>
              <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm">
                <p className="font-medium text-amber-600 dark:text-amber-400 mb-1">
                  {isRussian ? '⚠️ Внимание:' : '⚠️ Warning:'}
                </p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {!localAccessControl.registration_enabled && (
                    <li>{isRussian ? 'Новые пользователи не смогут зарегистрироваться' : 'New users will not be able to register'}</li>
                  )}
                  {!localAccessControl.guest_access_enabled && (
                    <li>{isRussian ? 'Неавторизованные пользователи будут перенаправлены на страницу входа' : 'Unauthorized users will be redirected to the login page'}</li>
                  )}
                  {localAccessControl.start_page === 'auth' && (
                    <li>{isRussian ? 'Все пользователи будут попадать на страницу авторизации' : 'All users will land on the authentication page'}</li>
                  )}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isRussian ? 'Отмена' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                await saveAccessControl();
                setAccessControlConfirmOpen(false);
              }}
            >
              {isRussian ? 'Применить изменения' : 'Apply changes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ban User Dialog */}
      <AlertDialog open={banDialogOpen} onOpenChange={setBanDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-amber-500" />
              {isRussian ? 'Заблокировать пользователя?' : 'Ban user?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                {isRussian 
                  ? `Пользователь "${selectedUser?.display_name || 'Unknown'}" будет переведён в режим read-only.`
                  : `User "${selectedUser?.display_name || 'Unknown'}" will be set to read-only mode.`
                }
              </p>
              <div className="flex items-center gap-2">
                <Label>{isRussian ? 'Срок (дней):' : 'Duration (days):'}</Label>
                <Select value={String(banDays)} onValueChange={(v) => setBanDays(Number(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="7">7</SelectItem>
                    <SelectItem value="14">14</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="90">90</SelectItem>
                    <SelectItem value="365">365</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleBanUser}>
              {isRussian ? 'Заблокировать' : 'Ban'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              {isRussian ? 'Удалить пользователя?' : 'Delete user?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-2">
                {isRussian 
                  ? `Вы собираетесь БЕЗВОЗВРАТНО удалить пользователя "${selectedUser?.display_name || 'Unknown'}". Все данные будут утеряны.`
                  : `You are about to PERMANENTLY delete user "${selectedUser?.display_name || 'Unknown'}". All data will be lost.`
                }
              </p>
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
                {isRussian ? '⚠️ Это действие необратимо!' : '⚠️ This action is irreversible!'}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isRussian ? 'Удалить навсегда' : 'Delete forever'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
